"use client";

import { useEffect, useRef, useState } from "react";
import { Notice } from "@/components/tool-kit/primitives";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { formatDateTime, formatNumber } from "@/lib/format-number";
import { t, tPlural } from "@/lib/i18n/interpolate";
import { getLocaleMeta } from "@/lib/i18n/locales";
import {
  getWallTime,
  resolveWallTime,
  type WallTime,
} from "@/lib/tool-kit/date-math/zone-wall-time";

const RE_DATETIME_LOCAL = /^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/;

interface Zone {
  id: string;
  label: string;
}

const ZONES: Zone[] = [
  { id: "Pacific/Honolulu", label: "Honolulu (HST)" },
  { id: "America/Anchorage", label: "Anchorage (AKT)" },
  { id: "America/Los_Angeles", label: "Los Angeles (PT)" },
  { id: "America/Denver", label: "Denver (MT)" },
  { id: "America/Phoenix", label: "Phoenix (MST)" },
  { id: "America/Chicago", label: "Chicago (CT)" },
  { id: "America/New_York", label: "New York (ET)" },
  { id: "America/Toronto", label: "Toronto (ET)" },
  { id: "America/Mexico_City", label: "Mexico City (CST)" },
  { id: "America/Bogota", label: "Bogotá (COT)" },
  { id: "America/Sao_Paulo", label: "São Paulo (BRT)" },
  { id: "America/Argentina/Buenos_Aires", label: "Buenos Aires (ART)" },
  { id: "UTC", label: "UTC" },
  { id: "Europe/London", label: "London (GMT/BST)" },
  { id: "Europe/Dublin", label: "Dublin (GMT/IST)" },
  { id: "Europe/Lisbon", label: "Lisbon (WET)" },
  { id: "Europe/Paris", label: "Paris (CET)" },
  { id: "Europe/Berlin", label: "Berlin (CET)" },
  { id: "Europe/Madrid", label: "Madrid (CET)" },
  { id: "Europe/Rome", label: "Rome (CET)" },
  { id: "Europe/Amsterdam", label: "Amsterdam (CET)" },
  { id: "Europe/Stockholm", label: "Stockholm (CET)" },
  { id: "Europe/Athens", label: "Athens (EET)" },
  { id: "Europe/Istanbul", label: "Istanbul (TRT)" },
  { id: "Europe/Moscow", label: "Moscow (MSK)" },
  { id: "Africa/Cairo", label: "Cairo (EET)" },
  { id: "Africa/Lagos", label: "Lagos (WAT)" },
  { id: "Africa/Johannesburg", label: "Johannesburg (SAST)" },
  { id: "Asia/Dubai", label: "Dubai (GST)" },
  { id: "Asia/Karachi", label: "Karachi (PKT)" },
  { id: "Asia/Kolkata", label: "Mumbai / Delhi (IST)" },
  { id: "Asia/Dhaka", label: "Dhaka (BST)" },
  { id: "Asia/Bangkok", label: "Bangkok (ICT)" },
  { id: "Asia/Singapore", label: "Singapore (SGT)" },
  { id: "Asia/Hong_Kong", label: "Hong Kong (HKT)" },
  { id: "Asia/Shanghai", label: "Shanghai / Beijing (CST)" },
  { id: "Asia/Tokyo", label: "Tokyo (JST)" },
  { id: "Asia/Seoul", label: "Seoul (KST)" },
  { id: "Australia/Perth", label: "Perth (AWST)" },
  { id: "Australia/Sydney", label: "Sydney (AET)" },
  { id: "Pacific/Auckland", label: "Auckland (NZT)" },
];

const MAX_TARGETS = 4;

/**
 * The reader's offset label ("GMT-5"). This one also runs through
 * `formatToParts`, like `getWallTime` above, but the part it pulls out is a
 * NAME for display, not a number fed back into a computation — it is never
 * re-parsed — so it takes the page locale rather than staying fixed.
 */
function getUtcOffsetLabel(
  date: Date,
  timeZone: string,
  locale: string
): string {
  const tag = getLocaleMeta(locale)?.hreflang ?? "en-US";
  const build = (t: string) =>
    new Intl.DateTimeFormat(t, {
      timeZone,
      timeZoneName: "shortOffset",
    }).formatToParts(date);
  let parts: Intl.DateTimeFormatPart[];
  try {
    parts = build(tag);
  } catch {
    // An Intl build without this locale's data must not take the tool down.
    parts = build("en-US");
  }
  return parts.find((p) => p.type === "timeZoneName")?.value ?? "";
}

function formatZoneTime(date: Date, timeZone: string, locale: string): string {
  return formatDateTime(locale, date, {
    timeZone,
    hour: "numeric",
    minute: "2-digit",
  });
}

function formatZoneDate(date: Date, timeZone: string, locale: string): string {
  return formatDateTime(locale, date, {
    timeZone,
    weekday: "short",
    month: "short",
    day: "numeric",
    year: "numeric",
  });
}

function zoneLabel(id: string): string {
  return ZONES.find((z) => z.id === id)?.label ?? id;
}

function toDatetimeLocalValue(date: Date): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
}

function parseDatetimeLocal(value: string): WallTime | null {
  const match = RE_DATETIME_LOCAL.exec(value);
  if (match === null) {
    return null;
  }
  return {
    year: Number.parseInt(match[1], 10),
    month: Number.parseInt(match[2], 10),
    day: Number.parseInt(match[3], 10),
    hour: Number.parseInt(match[4], 10),
    minute: Number.parseInt(match[5], 10),
  };
}

/** English strings. A translated content file overrides these through `ui`. */
export const TIME_ZONE_CONVERTER_STRINGS = {
  sourceTime: "Source time",
  dateAndTime: "Date & time",
  inTimeZone: "In time zone",
  isCurrently: "is currently",
  onThatDate: "on that date.",
  convertTo: "Convert to",
  targetZoneAriaLabel: "Target zone {n}",
  remove: "Remove",
  addTimeZone: "+ Add time zone",
  sameDay: "same day",
  dayPlural: "one: day|other: days",
  gapNotice:
    "{zone} skips this clock time when its clocks go forward, so it does not exist. The conversion moves it forward by the length of the jump.",
  overlapNotice:
    "{zone} repeats this clock time when its clocks go back. The conversion uses the first of the two occurrences.",
  footerNote:
    "Conversions use your browser's built-in IANA time zone database (via the Intl API), so results depend on how up to date your browser or operating system is. Nothing is sent to a server.",
} as const;

function describeDayOffset(
  source: WallTime,
  target: WallTime,
  strings: typeof TIME_ZONE_CONVERTER_STRINGS,
  locale: string
): string {
  const sourceDay = Date.UTC(source.year, source.month - 1, source.day);
  const targetDay = Date.UTC(target.year, target.month - 1, target.day);
  const diff = Math.round((targetDay - sourceDay) / 86_400_000);
  if (diff === 0) {
    return strings.sameDay;
  }
  if (diff > 0) {
    return `+${formatNumber(locale, diff)} ${tPlural(strings.dayPlural, locale, diff)}`;
  }
  return `−${formatNumber(locale, -diff)} ${tPlural(strings.dayPlural, locale, -diff)}`;
}

function TargetCard({
  instant,
  sourceWall,
  zoneId,
  onRemove,
  removable,
  strings,
  locale,
}: {
  instant: Date;
  sourceWall: WallTime;
  zoneId: string;
  onRemove: () => void;
  removable: boolean;
  strings: typeof TIME_ZONE_CONVERTER_STRINGS;
  locale: string;
}) {
  const targetWall = getWallTime(instant, zoneId);
  return (
    <div className="space-y-1 rounded-lg border border-border bg-card p-5">
      <div className="flex items-start justify-between gap-3">
        <p className="font-medium text-foreground">{zoneLabel(zoneId)}</p>
        {removable && (
          <button
            className="text-muted-foreground text-xs underline-offset-4 hover:text-foreground hover:underline"
            onClick={onRemove}
            type="button"
          >
            {strings.remove}
          </button>
        )}
      </div>
      <p className="font-mono font-semibold text-2xl text-foreground">
        {formatZoneTime(instant, zoneId, locale)}
      </p>
      <p className="text-muted-foreground text-sm">
        {formatZoneDate(instant, zoneId, locale)} ·{" "}
        {describeDayOffset(sourceWall, targetWall, strings, locale)}
      </p>
      <p className="text-muted-foreground text-xs">
        {getUtcOffsetLabel(instant, zoneId, locale)}
      </p>
    </div>
  );
}

interface TargetSlot {
  uid: number;
  zone: string;
}

export default function TimeZoneConverterWidget() {
  const strings = useToolUi(TIME_ZONE_CONVERTER_STRINGS);
  const locale = useToolLocale();
  const [dateValue, setDateValue] = useState("");
  const [sourceZone, setSourceZone] = useState("America/New_York");
  const [targets, setTargets] = useState<TargetSlot[]>([
    { uid: 1, zone: "Europe/London" },
    { uid: 2, zone: "Asia/Tokyo" },
  ]);
  const nextUidRef = useRef(3);

  // Default to the current local time on the client only, after hydration,
  // so the server-rendered HTML never disagrees with the browser.
  useEffect(() => {
    setDateValue((current) => {
      if (current.length > 0) {
        return current;
      }
      return toDatetimeLocalValue(new Date());
    });
  }, []);

  const sourceWall = parseDatetimeLocal(dateValue);
  const resolved =
    sourceWall === null ? null : resolveWallTime(sourceWall, sourceZone);
  const instant = resolved === null ? null : resolved.instant;

  function handleAddTarget() {
    if (targets.length >= MAX_TARGETS) {
      return;
    }
    const used = new Set([sourceZone, ...targets.map((t) => t.zone)]);
    const next = ZONES.find((z) => !used.has(z.id));
    if (next !== undefined) {
      const uid = nextUidRef.current;
      nextUidRef.current += 1;
      setTargets([...targets, { uid, zone: next.id }]);
    }
  }

  function handleRemoveTarget(uid: number) {
    setTargets(targets.filter((t) => t.uid !== uid));
  }

  function handleTargetChange(uid: number, zoneId: string) {
    setTargets(
      targets.map((t) => (t.uid === uid ? { ...t, zone: zoneId } : t))
    );
  }

  return (
    <>
      {/* Source */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.sourceTime}
        </h2>
        <div className="flex flex-wrap items-end gap-3">
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="tz-datetime"
            >
              {strings.dateAndTime}
            </label>
            <input
              className="rounded-lg border border-border bg-card p-3 font-mono text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="tz-datetime"
              onChange={(e) => setDateValue(e.target.value)}
              type="datetime-local"
              value={dateValue}
            />
          </div>
          <div className="space-y-2">
            <label
              className="block font-medium text-foreground text-sm"
              htmlFor="tz-source"
            >
              {strings.inTimeZone}
            </label>
            <select
              className="rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
              id="tz-source"
              onChange={(e) => setSourceZone(e.target.value)}
              value={sourceZone}
            >
              {ZONES.map((z) => (
                <option key={z.id} value={z.id}>
                  {z.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {resolved !== null && resolved.kind !== "ok" && (
          <Notice>
            {t(
              resolved.kind === "gap"
                ? strings.gapNotice
                : strings.overlapNotice,
              { zone: zoneLabel(sourceZone) }
            )}
          </Notice>
        )}
        {instant !== null && (
          <p className="text-muted-foreground text-xs">
            {zoneLabel(sourceZone)} {strings.isCurrently}{" "}
            {getUtcOffsetLabel(instant, sourceZone, locale)}{" "}
            {strings.onThatDate}
          </p>
        )}
      </section>

      {/* Targets */}
      <section className="space-y-3">
        <h2 className="font-semibold text-foreground text-xl">
          {strings.convertTo}
        </h2>
        <div className="space-y-3">
          {targets.map((target, index) => (
            <div className="space-y-3" key={target.uid}>
              <select
                aria-label={strings.targetZoneAriaLabel.replace(
                  "{n}",
                  String(index + 1)
                )}
                className="rounded-lg border border-border bg-card p-3 text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-primary"
                onChange={(e) => handleTargetChange(target.uid, e.target.value)}
                value={target.zone}
              >
                {ZONES.map((z) => (
                  <option key={z.id} value={z.id}>
                    {z.label}
                  </option>
                ))}
              </select>
              {instant !== null && sourceWall !== null && (
                <TargetCard
                  instant={instant}
                  locale={locale}
                  onRemove={() => handleRemoveTarget(target.uid)}
                  removable={targets.length > 1}
                  sourceWall={sourceWall}
                  strings={strings}
                  zoneId={target.zone}
                />
              )}
            </div>
          ))}
        </div>
        <button
          className="rounded-lg border border-border bg-card px-5 py-2 font-medium text-foreground text-sm transition-colors hover:bg-muted disabled:opacity-40"
          disabled={targets.length >= MAX_TARGETS}
          onClick={handleAddTarget}
          type="button"
        >
          {strings.addTimeZone}
        </button>
        <p className="text-muted-foreground text-xs">{strings.footerNote}</p>
      </section>
    </>
  );
}
